import { useSyncExternalStore } from 'react';

import { CacheKey } from '../../utils/common/constants';
import {
  dismissOutdatedClientPrompt,
  getReadsOnlyPromptOpenedAt,
  isOutdatedClientPromptOpen,
  isWritePromptOpen,
  subscribeToOutdatedClient,
} from '../../utils/common/outdatedClient';
import reloadDocument from '../../utils/common/reloadDocument';
import { ReloadHoldKind } from '../../utils/common/reloadHolds';
import { allowUnload } from '../../utils/react/hooks/useRefreshWarning';
import useReloadHold from '../../utils/react/hooks/useReloadHold';
import useReloadWhenQuiet from '../../utils/react/hooks/useReloadWhenQuiet';
import Modal from '../Modal';

/**
 * How long the prompt waits unanswered, with nobody using the tab and only
 * reads turned away, before reloading it.
 */
export const UNANSWERED_RELOAD_MS = 2 * 60 * 1000;

/**
 * The least time after a reload from the prompt before it reloads by itself,
 * so a reload that lands on the build it left cannot loop.
 */
export const PROMPT_RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

const markReloaded = () => {
  sessionStorage.setItem(CacheKey.OutdatedClientReloadedAt, String(Date.now()));
};

const claimReload = () => {
  try {
    const last = Number(sessionStorage.getItem(CacheKey.OutdatedClientReloadedAt));
    if (Date.now() - last < PROMPT_RELOAD_COOLDOWN_MS) {
      return false;
    }
    markReloaded();
    return true;
  } catch {
    // Storage that cannot be read or written cannot bound a reload loop
    return false;
  }
};

const reload = () => {
  try {
    markReloaded();
  } catch {
    // A reload the reader asked for goes ahead without the mark
  }
  allowUnload();
  reloadDocument();
};

/**
 * Asks the reader to reload once the API turns this build away. Dismissing it
 * lets them copy out what they typed first, since a request turned away saved
 * nothing. Left unanswered on a tab nobody is using, where only reads were
 * turned away, it reloads by itself; over a write turned away, it holds every
 * reload a quiet tab would take, a newer build's included.
 */
const OutdatedClientModal = () => {
  const open = useSyncExternalStore(subscribeToOutdatedClient, isOutdatedClientPromptOpen);
  const isOverWrite = useSyncExternalStore(subscribeToOutdatedClient, isWritePromptOpen);
  useReloadHold(isOverWrite, ReloadHoldKind.Watched);

  useReloadWhenQuiet({
    getWantedSince: getReadsOnlyPromptOpenedAt,
    quietForMs: UNANSWERED_RELOAD_MS,
    claim: claimReload,
  });

  return (
    <Modal
      open={open}
      hideCloseIcon
      title='Cognito has been updated'
      description={
        "This page is out of date, so changes here can't be saved.\nCopy anything you need from the page, then reload."
      }
      buttons={[
        { label: 'Not yet', color: 'gray', onClick: dismissOutdatedClientPrompt },
        { label: 'Reload now', color: 'primary', onClick: reload },
      ]}
    />
  );
};

export default OutdatedClientModal;
